import { useEffect, useRef } from 'react';
import { useApolloClient, useSubscription } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { mdiCheck, mdiClose } from '@mdi/js';

import { Button } from 'common/components/Button';
import { CircularProgress } from 'common/components/CircularProgress';
import { Icon } from 'common/components/Icon';
import { parseJSON } from 'common/utils/json';
import { ITaskStateEnum, TaskStatusDocument } from 'graphql/types';

interface IProps {
    taskId: string;
    onRetry(): void;
    onClose(): void;
    onSuccess?(succeededCount: number): void;
}

export const UserImportTaskStatus = ({
    taskId,
    onRetry,
    onClose,
    onSuccess,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;

    const { data: taskStatusData, error: subscriptionError } = useSubscription(
        TaskStatusDocument,
        {
            variables: { id: taskId },
            skip: !taskId,
        }
    );

    const { state, result } = taskStatusData?.taskStatus || {};

    const isPending = state === ITaskStateEnum.Pending;
    const isStarted = state === ITaskStateEnum.Started;
    const isSuccess = state === ITaskStateEnum.Success;
    const isFailed = state === ITaskStateEnum.Failure;

    const parsedResult = parseJSON(result ?? '');
    const processedCount = parsedResult?.processed_count || 0;
    const totalCount = parsedResult?.total_count || 0;
    const succeededCount = parsedResult?.succeeded_count || 0;

    useEffect(() => {
        if (isSuccess) {
            client.cache.evict({ fieldName: 'paginatedUsers' });
            client.cache.gc();
            onSuccessRef.current?.(succeededCount);
        }
    }, [isSuccess, client.cache, succeededCount]);

    const hasError = isFailed || !!subscriptionError;
    const isLoading = (!taskStatusData && !hasError) || isPending || isStarted;
    const showRetry = hasError;

    const statusMessage = (() => {
        if (isPending || (!taskStatusData && !hasError)) {
            return translate('userImportModal.taskStatus.pendingMessage');
        }

        if (isStarted) {
            return translate('userImportModal.taskStatus.progressCount', {
                processedCount,
                totalCount,
            });
        }

        if (isSuccess) {
            return translate('userImportModal.taskStatus.finishedMessage', {
                succeededCount,
                totalCount,
            });
        }

        if (hasError) {
            return translate('userImportModal.taskStatus.failedMessage');
        }

        return null;
    })();

    return (
        <Stack spacing={2} sx={{ mx: 'auto' }}>
            {isLoading && (
                <>
                    <Typography variant="body2">
                        {translate('userImportModal.taskStatus.infoMessage')}
                    </Typography>

                    <Divider />
                </>
            )}

            <Stack alignItems="center" direction="row" spacing={1}>
                {isLoading && <CircularProgress size={19} />}

                {isSuccess && (
                    <Icon color="success" path={mdiCheck} size="1.9rem" />
                )}

                {hasError && (
                    <Icon color="error" path={mdiClose} size="1.9rem" />
                )}

                <Typography
                    role={hasError ? 'alert' : undefined}
                    variant="body2"
                >
                    {statusMessage}
                </Typography>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={1}>
                {showRetry && (
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onRetry}
                    >
                        {translate('userImportModal.taskStatus.retryButton')}
                    </Button>
                )}

                <Button variant="contained" onClick={onClose}>
                    {translate('userImportModal.taskStatus.closeButton')}
                </Button>
            </Stack>
        </Stack>
    );
};
